import React, { useState, useEffect, useMemo } from "react";
import { DragDropContext, Droppable, Draggable, DropResult } from "react-beautiful-dnd";
import ResourceModal from "./ResourceModal";
import ResourceSidePanel from "./ResourceSidePanel";
import ApiService from "../../_services/ApiService";
import type {
  LearningModule,
  FlipClassPhaseConfig,
  ResourceWithContext,
  Resource,
  Tag,
  ResourceType,
  Step,
} from "./types";

type FlipClassBoardTabProps = {
  module: LearningModule | null;
  setModule: React.Dispatch<React.SetStateAction<LearningModule | null>>;
  availableTags: Tag[];
  resourceTypes: ResourceType[];
  onSave: () => Promise<void>;
  isViewMode: boolean;
  onGenerateResources?: (chapterIndex: number, sectionIndex: number) => void;
  onGenerateAiQuiz?: (chapterIndex: number, sectionIndex: number) => void;
  onBatchGenerateAiQuiz?: (phase: string, phaseTags: Tag[]) => void;
  onOpenAiGenerateModal?: (type: "resources" | "quiz" | "quiz-all", opts: { chapterIndex?: number; sectionIndex?: number; phaseName?: string; phaseTags?: Tag[] }) => void;
  aiLoadingStates?: Record<string, boolean>;
  aiConfiguredResourceIds?: Set<number>;
};

export default function FlipClassBoardTab({
  module,
  setModule,
  availableTags,
  resourceTypes,
  onSave,
  isViewMode,
  onGenerateResources,
  onGenerateAiQuiz,
  onBatchGenerateAiQuiz,
  onOpenAiGenerateModal,
  aiLoadingStates = {},
  aiConfiguredResourceIds,
}: FlipClassBoardTabProps) {
  const [phaseConfigs, setPhaseConfigs] = useState<FlipClassPhaseConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedResource, setSelectedResource] = useState<ResourceWithContext | null>(null);
  const [resourceModalOpen, setResourceModalOpen] = useState(false);
  const [sidePanelResource, setSidePanelResource] = useState<ResourceWithContext | null>(null);

  // Resolve phase tag IDs to full Tag objects and open AI generate modal (or fallback to direct call)
  const handleBatchQuizForPhase = (config: FlipClassPhaseConfig) => {
    const phaseTags: Tag[] = config.tags
      .map((tagId) => availableTags.find((t) => t.id === tagId))
      .filter((t): t is Tag => t !== undefined);
    if (onOpenAiGenerateModal) {
      onOpenAiGenerateModal("quiz-all", { phaseName: config.phase, phaseTags });
    } else if (onBatchGenerateAiQuiz) {
      onBatchGenerateAiQuiz(config.phase, phaseTags);
    }
  };

  // Fetch flip-class configuration on mount
  useEffect(() => {
    const fetchConfig = async () => {
      try {
        setLoading(true);
        const response = await ApiService.fetchFlipClassConfig();
        if (response.data && Array.isArray(response.data)) {
          // Sort by sortOrder
          const sorted = [...(response.data as FlipClassPhaseConfig[])].sort((a, b) => a.sortOrder - b.sortOrder);
          setPhaseConfigs(sorted);
        } else {
          setError("No flip-class configuration found");
        }
      } catch (err: any) {
        console.error("Failed to fetch flip-class config:", err);
        setError("Failed to load flip-class configuration");
      } finally {
        setLoading(false);
      }
    };

    fetchConfig();
  }, []);

  // Create a stable key for DragDropContext to prevent stale state
  const dndKey = useMemo(() => {
    return phaseConfigs.length > 0 ? phaseConfigs.map(c => c.id).join('-') : 'empty';
  }, [phaseConfigs]);

  // Flatten all steps with chapter context for matrix rows
  type StepWithContext = {
    step: Step;
    chapterIndex: number;
    sectionIndex: number;
    chapterName: string;
    chapterColor: string;
  };

  const allSteps = useMemo((): StepWithContext[] => {
    if (!module) return [];

    const steps: StepWithContext[] = [];
    module.groups.forEach((chapter, chIdx) => {
      chapter.steps.forEach((section, sIdx) => {
        steps.push({
          step: section,
          chapterIndex: chIdx,
          sectionIndex: sIdx,
          chapterName: chapter.name,
          chapterColor: chapter.color,
        });
      });
    });
    return steps;
  }, [module]);

  // Create matrix: Map<stepId, Map<phaseId, ResourceWithContext[]>>
  const resourceMatrix = useMemo(() => {
    if (!module) {
      return new Map<string, Map<string, ResourceWithContext[]>>();
    }

    const matrix = new Map<string, Map<string, ResourceWithContext[]>>();

    // Initialize matrix for all steps and phases
    allSteps.forEach((stepContext) => {
      const stepId = `${stepContext.chapterIndex}-${stepContext.sectionIndex}`;
      const phaseMap = new Map<string, ResourceWithContext[]>();

      phaseConfigs.forEach((config) => {
        phaseMap.set(config.phase, []);
      });
      phaseMap.set("untagged", []); // Special column for untagged resources

      matrix.set(stepId, phaseMap);
    });

    // Fill matrix with resources
    module.groups.forEach((chapter, chIdx) => {
      chapter.steps.forEach((section, sIdx) => {
        const stepId = `${chIdx}-${sIdx}`;
        const phaseMap = matrix.get(stepId);

        if (!phaseMap) return;

        section.resources.forEach((resource, rIdx) => {
          const resourceWithContext: ResourceWithContext = {
            ...resource,
            chapterIndex: chIdx,
            sectionIndex: sIdx,
            resourceIndex: rIdx,
            chapterName: chapter.name,
            sectionName: section.name,
          };

          // Check which phase this resource belongs to
          let addedToPhase = false;

          if (resource.tags && resource.tags.length > 0) {
            for (const phaseConfig of phaseConfigs) {
              const hasPhaseTag = resource.tags.some((tag) => phaseConfig.tags.includes(tag.id));

              if (hasPhaseTag) {
                phaseMap.get(phaseConfig.phase)!.push(resourceWithContext);
                addedToPhase = true;
                break; // Only add to first matching phase
              }
            }
          }

          if (!addedToPhase) {
            phaseMap.get("untagged")!.push(resourceWithContext);
          }
        });
      });
    });

    return matrix;
  }, [module, phaseConfigs, allSteps]);

  // Handle drag start (for debugging)
  const handleDragStart = (result: any) => {
    console.log("[Board] Drag started:", result.draggableId);
  };

  // Handle drag and drop (2D: can move between steps AND phases)
  const handleDragEnd = async (result: DropResult) => {
    const { source, destination, draggableId } = result;

    console.log("[Matrix] Drag ended:", { source, destination, draggableId });

    // Dropped outside a valid droppable
    if (!destination) {
      console.log("[Matrix] Dropped outside valid area");
      return;
    }

    // Dropped in the same place
    if (source.droppableId === destination.droppableId && source.index === destination.index) {
      console.log("[Matrix] Dropped in same place");
      return;
    }

    // Parse droppableId format: "cell-{chapterIdx}-{sectionIdx}-{phase}"
    const parseDroppableId = (id: string) => {
      const parts = id.split("-");
      return {
        chapterIndex: parseInt(parts[1]),
        sectionIndex: parseInt(parts[2]),
        phase: parts[3],
      };
    };

    // Parse draggableId format: "resource-{chapterIdx}-{sectionIdx}-{resourceIdx}"
    const parts = draggableId.split("-");
    const sourceChapterIndex = parseInt(parts[1]);
    const sourceSectionIndex = parseInt(parts[2]);
    const resourceIndex = parseInt(parts[3]);

    const sourceCell = parseDroppableId(source.droppableId);
    const destCell = parseDroppableId(destination.droppableId);

    console.log("[Matrix] Moving resource from step", sourceCell.chapterIndex, sourceCell.sectionIndex, sourceCell.phase);
    console.log("[Matrix] Moving resource to step", destCell.chapterIndex, destCell.sectionIndex, destCell.phase);

    if (!module) return;

    // Clone module to update
    const updatedModule = JSON.parse(JSON.stringify(module)) as LearningModule;

    // Remove resource from source step
    const resource = updatedModule.groups[sourceChapterIndex].steps[sourceSectionIndex].resources.splice(
      resourceIndex,
      1
    )[0];

    // Update resource tags based on destination phase
    resource.tags = (resource.tags || []).filter(
      (tag) => !phaseConfigs.some((pc) => pc.tags.includes(tag.id))
    );

    if (destCell.phase !== "untagged") {
      const destPhaseConfig = phaseConfigs.find((c) => c.phase === destCell.phase);
      if (destPhaseConfig) {
        destPhaseConfig.tags.forEach((tagId) => {
          const tag = availableTags.find((t) => t.id === tagId);
          if (tag && !resource.tags!.find((t) => t.id === tag.id)) {
            resource.tags!.push(tag);
          }
        });
      }
    }

    // Add resource to destination step
    updatedModule.groups[destCell.chapterIndex].steps[destCell.sectionIndex].resources.push(resource);

    // Optimistic update
    setModule(updatedModule);

    // Save to backend
    try {
      await onSave();
    } catch (error) {
      console.error("Failed to save:", error);
      // Revert on failure
      setModule(module);
      alert("Failed to move resource. Please try again.");
    }
  };

  // Handle add resource to a specific step and phase
  const handleAddResourceToCell = (chapterIndex: number, sectionIndex: number, phase: string) => {
    if (!module) return;

    // Create a new empty resource
    const newResource: Resource = {
      name: "New Resource",
      resourceType: "studyResource",
      studyResourceTypeId: 13, // Default to PDF type
      content: "",
      sortOrder: 0,
      tags: [],
    };

    // Add phase tags if not untagged
    if (phase !== "untagged") {
      const phaseConfig = phaseConfigs.find((c) => c.phase === phase);
      if (phaseConfig) {
        phaseConfig.tags.forEach((tagId) => {
          const tag = availableTags.find((t) => t.id === tagId);
          if (tag) {
            newResource.tags!.push(tag);
          }
        });
      }
    }

    // Open modal to edit the new resource WITHOUT adding it to the module yet
    // The resource will be added only when user saves in the modal
    const resourceIndex = module.groups[chapterIndex].steps[sectionIndex].resources.length;
    const resourceWithContext: ResourceWithContext = {
      ...newResource,
      chapterIndex,
      sectionIndex,
      resourceIndex, // This will be the index where it will be added
      chapterName: module.groups[chapterIndex].name,
      sectionName: module.groups[chapterIndex].steps[sectionIndex].name,
    };

    setSelectedResource(resourceWithContext);
    setResourceModalOpen(true);
  };

  // Handle edit resource
  const handleEditResource = (resource: ResourceWithContext) => {
    setSelectedResource(resource);
    setResourceModalOpen(true);
  };

  // Handle resource card click - opens side panel
  const handleResourceClick = (resource: ResourceWithContext) => {
    setSidePanelResource(resource);
  };

  // Handle edit from side panel
  const handleEditFromSidePanel = () => {
    if (sidePanelResource) {
      setSelectedResource(sidePanelResource);
      setResourceModalOpen(true);
      setSidePanelResource(null);
    }
  };

  // Handle delete resource
  const handleDeleteResource = async (resource: ResourceWithContext) => {
    if (!window.confirm(`Are you sure you want to delete "${resource.name}"?`)) {
      return;
    }

    if (!module) return;

    // Get the step from the module
    const step = module.groups[resource.chapterIndex].steps[resource.sectionIndex];

    // Check if this is a saved resource (has id) or freshly added (no id)
    const isSavedResource = resource.id || resource.mappingId;

    // If it's a saved resource, delete the mapping from database
    if (isSavedResource && step.id) {
      try {
        // Call the delete API to remove the database mapping
        const deletePayload: {
          stepId: number;
          studyResourceId?: number;
          testId?: number;
        } = {
          stepId: step.id,
        };

        if (resource.resourceType === "test" && resource.testId) {
          deletePayload.testId = Number(resource.testId);
        } else if (resource.studyResourceId || resource.id) {
          deletePayload.studyResourceId = resource.studyResourceId || resource.id;
        }

        console.log("[handleDeleteResource] Deleting saved resource mapping:", deletePayload);
        await ApiService.deleteStepResourceMapping(deletePayload);
        console.log("[handleDeleteResource] Mapping deleted successfully");
      } catch (error) {
        console.error("[handleDeleteResource] Failed to delete mapping:", error);
        alert("Failed to delete resource mapping from database. Please try again.");
        return;
      }
    } else {
      console.log("[handleDeleteResource] Deleting freshly added resource (no API call needed)");
    }

    // Remove from JSON state
    const updatedModule = JSON.parse(JSON.stringify(module)) as LearningModule;
    updatedModule.groups[resource.chapterIndex].steps[resource.sectionIndex].resources.splice(
      resource.resourceIndex,
      1
    );

    // Update state immediately
    setModule(updatedModule);

    // Only trigger full save if this was a saved resource
    // For freshly added resources, just updating state is enough
    if (isSavedResource) {
      console.log("[handleDeleteResource] Triggering full lesson plan save after deleting saved resource");
      onSave().catch((error) => {
        console.error("Failed to save lesson plan after deletion:", error);
        setModule(module);
        alert("Failed to update lesson plan after deletion. Please try again.");
      });
    }
  };

  // Handle resource modal updates
  const handleUpdateResource = (
    chapterIndex: number,
    sectionIndex: number,
    resourceIndex: number,
    updates: Partial<Resource>
  ) => {
    if (!module) return;

    const updatedModule = JSON.parse(JSON.stringify(module)) as LearningModule;
    const resources = updatedModule.groups[chapterIndex].steps[sectionIndex].resources;

    if (resourceIndex < resources.length) {
      Object.assign(resources[resourceIndex], updates);
    } else {
      // New resource added inside the modal — append to the step
      resources.push({ ...updates, sortOrder: resources.length + 1 } as Resource);
    }

    setModule(updatedModule);
    // Auto-save will be triggered by parent
  };

  const handleRemoveResource = (resourceIndex: number) => {
    if (!selectedResource) return;
    handleDeleteResource({ ...selectedResource, resourceIndex });
    setResourceModalOpen(false);
  };

  if (loading) {
    return (
      <div className="lmb-board-view">
        <div style={{ textAlign: "center", padding: "40px", color: "var(--text-muted)" }}>
          Loading flip-class configuration...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="lmb-board-view">
        <div style={{ textAlign: "center", padding: "40px", color: "var(--status-danger)" }}>
          <div style={{ fontSize: "48px", marginBottom: "16px" }}>⚠️</div>
          <div style={{ fontSize: "18px", fontWeight: 600, marginBottom: "8px" }}>{error}</div>
          <div style={{ fontSize: "14px", color: "var(--text-muted)" }}>
            Please check your database configuration or contact support.
          </div>
        </div>
      </div>
    );
  }

  if (!module) {
    return (
      <div className="lmb-board-view">
        <div style={{ textAlign: "center", padding: "40px", color: "var(--text-muted)" }}>
          No module loaded
        </div>
      </div>
    );
  }

  // Helper to get resource icon - checks URL and name for content type
  const getResourceIcon = (typeId: number, url?: string, name?: string): string => {
    // Check URL patterns first
    if (url) {
      const lowerUrl = url.toLowerCase();
      // Check for PDF files
      if (lowerUrl.endsWith(".pdf") || lowerUrl.includes(".pdf?") || lowerUrl.includes("/pdf/")) {
        return "📕"; // PDF icon
      }
      // Check for YouTube
      if (lowerUrl.includes("youtube.com") || lowerUrl.includes("youtu.be")) {
        return "▶️"; // YouTube
      }
      // Check for Vimeo
      if (lowerUrl.includes("vimeo.com")) {
        return "🎬"; // Vimeo
      }
    }

    // Check name patterns for quiz/test detection
    if (name) {
      const lowerName = name.toLowerCase();
      if (lowerName.includes("quiz") || lowerName.includes("test") || lowerName.includes("assessment") || lowerName.includes("exam")) {
        return "📋"; // Quiz/Test icon
      }
    }

    const iconMap: { [key: number]: string } = {
      1: "📥", // Download
      5: "🔗", // Reference
      6: "📝", // Article
      13: "📚", // E-Book
      16: "🎥", // Video
      17: "📋", // Quiz
      18: "🔗", // Link
      19: "📊", // Presentation
      20: "💻", // Code
      21: "📄", // Document
      22: "🖼️", // Image
      23: "🎵", // Audio
      25: "📋", // Lesson Plan
      31: "🎬", // Vimeo Video
      32: "▶️", // YouTube Video
      36: "📋", // Online Test
    };
    return iconMap[typeId] || "📄"; // Default to document icon
  };

  return (
    <div className="lmb-resources-matrix-view">
      <DragDropContext
        key={dndKey}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        <div className="lmb-matrix-container">
          <table className="lmb-matrix-table">
            <thead>
              <tr>
                <th className="lmb-matrix-step-header">Step</th>
                {phaseConfigs.map((config) => (
                  <th key={config.phase} className="lmb-matrix-phase-header">
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
                      <span>{config.description}</span>
                      {!isViewMode && onBatchGenerateAiQuiz && (
                        <button
                          className="lmb-btn lmb-btn-sm"
                          onClick={() => handleBatchQuizForPhase(config)}
                          disabled={!!aiLoadingStates[`batch-${config.phase}`]}
                          title={`Generate AI quizzes for all steps in ${config.description}`}
                          style={{
                            fontSize: 10,
                            padding: "2px 8px",
                            whiteSpace: "nowrap",
                            background: "var(--gradient-brand)",
                            color: "white",
                            border: "none",
                            borderRadius: 10,
                          }}
                        >
                          {aiLoadingStates[`batch-${config.phase}`] ? (
                            <><span className="lmb-loading"></span> Generating...</>
                          ) : (
                            "🤖 AI Quiz All"
                          )}
                        </button>
                      )}
                    </div>
                  </th>
                ))}
                <th className="lmb-matrix-phase-header lmb-matrix-untagged-header">
                  {phaseConfigs.length > 0 ? "Untagged" : "Resources"}
                </th>
              </tr>
            </thead>
            <tbody>
              {allSteps.map((stepContext) => {
                const stepId = `${stepContext.chapterIndex}-${stepContext.sectionIndex}`;
                const phaseMap = resourceMatrix.get(stepId);

                return (
                  <tr key={stepId} className="lmb-matrix-row">
                    {/* Step name column */}
                    <td className="lmb-matrix-step-cell">
                      <div className="lmb-step-info">
                        <div
                          className="lmb-chapter-badge"
                          style={{ backgroundColor: stepContext.chapterColor }}
                          title={stepContext.chapterName}
                        >
                          {stepContext.chapterName}
                        </div>
                        <div className="lmb-step-name" title={stepContext.step.name}>{stepContext.step.name}</div>
                        <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                          {!isViewMode && (onOpenAiGenerateModal || onGenerateResources) && (
                            <button
                              className="lmb-btn lmb-btn-sm"
                              onClick={() => {
                                if (onOpenAiGenerateModal) {
                                  onOpenAiGenerateModal("resources", { chapterIndex: stepContext.chapterIndex, sectionIndex: stepContext.sectionIndex });
                                } else if (onGenerateResources) {
                                  onGenerateResources(stepContext.chapterIndex, stepContext.sectionIndex);
                                }
                              }}
                              disabled={!!aiLoadingStates[stepId]}
                              title="Generate resources for this section with AI"
                              style={{ fontSize: 11, padding: "2px 6px", whiteSpace: "nowrap" }}
                            >
                              {aiLoadingStates[stepId] ? (
                                <><span className="lmb-loading"></span></>
                              ) : (
                                "✨ AI"
                              )}
                            </button>
                          )}
                          {!isViewMode && (onOpenAiGenerateModal || onGenerateAiQuiz) && (() => {
                            const hasContent = stepContext.step.resources.some(r =>
                              r.resourceType === "studyResource" && (
                                ((r.studyResourceTypeId === 13 || r.studyResourceTypeId === 1) && (r.url || r.fileUrl)) ||
                                (r.studyResourceTypeId === 6 && r.id)
                              )
                            );
                            const quizKey = `ai-quiz-${stepContext.chapterIndex}-${stepContext.sectionIndex}`;
                            return hasContent ? (
                              <button
                                className="lmb-btn lmb-btn-sm"
                                onClick={() => {
                                  if (onOpenAiGenerateModal) {
                                    onOpenAiGenerateModal("quiz", { chapterIndex: stepContext.chapterIndex, sectionIndex: stepContext.sectionIndex });
                                  } else if (onGenerateAiQuiz) {
                                    onGenerateAiQuiz(stepContext.chapterIndex, stepContext.sectionIndex);
                                  }
                                }}
                                disabled={!!aiLoadingStates[quizKey]}
                                title="Generate AI quiz from content in this section"
                                style={{
                                  fontSize: 11,
                                  padding: "2px 6px",
                                  whiteSpace: "nowrap",
                                  background: "var(--gradient-brand)",
                                  color: "white",
                                  border: "none",
                                }}
                              >
                                {aiLoadingStates[quizKey] ? (
                                  <><span className="lmb-loading"></span></>
                                ) : (
                                  "🤖 Quiz"
                                )}
                              </button>
                            ) : null;
                          })()}
                        </div>
                      </div>
                    </td>

                    {/* Phase columns */}
                    {phaseConfigs.map((config) => {
                      const resources = phaseMap?.get(config.phase) || [];
                      const cellId = `cell-${stepContext.chapterIndex}-${stepContext.sectionIndex}-${config.phase}`;

                      return (
                        <td key={config.phase} className="lmb-matrix-cell">
                          <Droppable droppableId={cellId} type="RESOURCE">
                            {(provided, snapshot) => (
                              <div
                                ref={provided.innerRef}
                                {...provided.droppableProps}
                                className={`lmb-cell-dropzone ${snapshot.isDraggingOver ? "dragging-over" : ""}`}
                              >
                                {resources.map((resource, index) => {
                                  const draggableId = `resource-${resource.chapterIndex}-${resource.sectionIndex}-${resource.resourceIndex}`;
                                  return (
                                    <Draggable
                                      key={draggableId}
                                      draggableId={draggableId}
                                      index={index}
                                      isDragDisabled={isViewMode}
                                    >
                                      {(provided, snapshot) => (
                                        <div
                                          ref={provided.innerRef}
                                          {...provided.draggableProps}
                                          {...provided.dragHandleProps}
                                          className={`lmb-matrix-resource-card ${snapshot.isDragging ? "dragging" : ""}`}
                                          style={{
                                            ...provided.draggableProps.style,
                                            cursor: isViewMode ? "pointer" : "grab",
                                          }}
                                          onClick={() => handleResourceClick(resource)}
                                        >
                                          <div className="lmb-resource-icon">
                                            {getResourceIcon(resource.studyResourceTypeId, resource.url, resource.name)}
                                          </div>
                                          <div className="lmb-resource-name" title={resource.name}>
                                            {resource.name}
                                          </div>
                                          {resource.id && resource.resourceType === 'studyResource' && aiConfiguredResourceIds?.has(resource.id) && (
                                            <span className="lmb-ai-config-badge" title="AI Workspace configured">✦</span>
                                          )}
                                          {!isViewMode && (
                                            <div className="lmb-resource-actions">
                                              <button
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  handleEditResource(resource);
                                                }}
                                                title="Edit resource"
                                              >
                                                ✏️
                                              </button>
                                              <button
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  handleDeleteResource(resource);
                                                }}
                                                title="Delete resource"
                                              >
                                                🗑️
                                              </button>
                                            </div>
                                          )}
                                        </div>
                                      )}
                                    </Draggable>
                                  );
                                })}
                                {provided.placeholder}
                                {!isViewMode && !snapshot.isDraggingOver && (
                                  <div
                                    className="lmb-empty-cell"
                                    onClick={() => handleAddResourceToCell(stepContext.chapterIndex, stepContext.sectionIndex, config.phase)}
                                    style={{ cursor: 'pointer' }}
                                  >
                                    + Add
                                  </div>
                                )}
                              </div>
                            )}
                          </Droppable>
                        </td>
                      );
                    })}

                    {/* Untagged column */}
                    <td className="lmb-matrix-cell lmb-matrix-untagged-cell">
                      {(() => {
                        const resources = phaseMap?.get("untagged") || [];
                        const cellId = `cell-${stepContext.chapterIndex}-${stepContext.sectionIndex}-untagged`;

                        return (
                          <Droppable droppableId={cellId} type="RESOURCE">
                            {(provided, snapshot) => (
                              <div
                                ref={provided.innerRef}
                                {...provided.droppableProps}
                                className={`lmb-cell-dropzone ${snapshot.isDraggingOver ? "dragging-over" : ""}`}
                              >
                                {resources.map((resource, index) => {
                                  const draggableId = `resource-${resource.chapterIndex}-${resource.sectionIndex}-${resource.resourceIndex}`;
                                  return (
                                    <Draggable
                                      key={draggableId}
                                      draggableId={draggableId}
                                      index={index}
                                      isDragDisabled={isViewMode}
                                    >
                                      {(provided, snapshot) => (
                                        <div
                                          ref={provided.innerRef}
                                          {...provided.draggableProps}
                                          {...provided.dragHandleProps}
                                          className={`lmb-matrix-resource-card ${snapshot.isDragging ? "dragging" : ""}`}
                                          style={{
                                            ...provided.draggableProps.style,
                                            cursor: isViewMode ? "pointer" : "grab",
                                          }}
                                          onClick={() => handleResourceClick(resource)}
                                        >
                                          <div className="lmb-resource-icon">
                                            {getResourceIcon(resource.studyResourceTypeId, resource.url, resource.name)}
                                          </div>
                                          <div className="lmb-resource-name" title={resource.name}>
                                            {resource.name}
                                          </div>
                                          {resource.id && resource.resourceType === 'studyResource' && aiConfiguredResourceIds?.has(resource.id) && (
                                            <span className="lmb-ai-config-badge" title="AI Workspace configured">✦</span>
                                          )}
                                          {!isViewMode && (
                                            <div className="lmb-resource-actions">
                                              <button
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  handleEditResource(resource);
                                                }}
                                                title="Edit resource"
                                              >
                                                ✏️
                                              </button>
                                              <button
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  handleDeleteResource(resource);
                                                }}
                                                title="Delete resource"
                                              >
                                                🗑️
                                              </button>
                                            </div>
                                          )}
                                        </div>
                                      )}
                                    </Draggable>
                                  );
                                })}
                                {provided.placeholder}
                                {!isViewMode && !snapshot.isDraggingOver && (
                                  <div
                                    className="lmb-empty-cell"
                                    onClick={() => handleAddResourceToCell(stepContext.chapterIndex, stepContext.sectionIndex, "untagged")}
                                    style={{ cursor: 'pointer' }}
                                  >
                                    + Add
                                  </div>
                                )}
                              </div>
                            )}
                          </Droppable>
                        );
                      })()}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </DragDropContext>

      {/* Resource Edit Modal */}
      {selectedResource && (
        <ResourceModal
          isOpen={resourceModalOpen}
          onClose={() => setResourceModalOpen(false)}
          resources={[selectedResource]}
          sectionName={selectedResource.sectionName}
          chapterName={selectedResource.chapterName}
          chapterIndex={selectedResource.chapterIndex}
          sectionIndex={selectedResource.sectionIndex}
          onRemoveResource={handleRemoveResource}
          onUpdateResource={(ci, si, localIndex, updates) => {
            if (localIndex === 0) {
              // Map modal's index 0 to the actual resource index in the module
              handleUpdateResource(ci, si, selectedResource!.resourceIndex, updates);
            } else {
              // New resource added inside the modal — append via out-of-bounds index
              const stepResources = module!.groups[ci].steps[si].resources;
              handleUpdateResource(ci, si, stepResources.length, updates);
            }
          }}
          availableTags={availableTags}
          resourceTypes={resourceTypes}
          isViewMode={isViewMode}
        />
      )}

      {/* Resource Side Panel */}
      <ResourceSidePanel
        isOpen={sidePanelResource !== null}
        onClose={() => setSidePanelResource(null)}
        resource={sidePanelResource}
        onEdit={handleEditFromSidePanel}
        isViewMode={isViewMode}
        resourceTypes={resourceTypes}
      />
    </div>
  );
}
